/**
 * components/RunProgress.tsx — what a publish in flight looks like, and what it looks like when it lands.
 *
 * The counters here are the server's own, re-read every few seconds while the run holds its lease, so this
 * is genuine progress rather than a spinner with a reassuring caption: repositories covered climbs as they
 * are fetched, commits seen and dropped climb as the significance filter works through them, items
 * published lands at the end. A run that has stalled therefore *looks* stalled, which is the point.
 *
 * The elapsed clock is computed from `startedAt`, not from a timer started when the button was pressed, so
 * it is right after a refresh and right for a second person watching the same run.
 *
 * There is deliberately no percentage. Nothing knows the denominator until the commits have been fetched,
 * and a bar that jumps to 90% and sits there is a worse answer than four honest numbers.
 *
 * **Every sentence here used to presume that a commit is the unit of work**, which was true of the only
 * kind of update that existed. It is not true of the two document kinds: they read one file, make one
 * model call and compose one page, and their commit counters are all zero for the excellent reason that
 * they never looked at a commit. Reported in the changelog's words, a perfectly good executive summary
 * reads as a run that found nothing — "0 commits, none of them worth reporting" — which is the
 * application libelling its own work. So each panel takes the kind and says what that kind actually did.
 * `kind` DEFAULTS to `changelog`, and the default is safe rather than merely convenient: it is what every
 * update was before there were three, and what the server still stores for one that says nothing.
 */
import { useEffect, useState } from "react";
import type { Run, UpdateKind } from "../lib/api";
import { formatCount, formatDateTime, formatDuration } from "../lib/format";
import { Badge, Banner, Card, Spinner } from "./ui";

/** Ticks once a second so the elapsed time moves; the run's own data comes from the poll, not from here. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

function elapsed(startedAt: string, now: number): string {
  const ms = Math.max(0, now - new Date(startedAt).getTime());
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
}

export interface RunProgressProps {
  readonly run: Run;
  /** The heading names the update, so this panel makes sense on a screen listing several. */
  readonly jobName?: string;
  /** What this update says. Decides which counters mean anything; see the header. */
  readonly kind?: UpdateKind;
}

/** A run in flight. */
export function RunProgress({ run, jobName, kind = "changelog" }: RunProgressProps): JSX.Element {
  const now = useNow(true);

  return (
    <Card
      title={jobName === undefined ? "Publishing now" : `Publishing “${jobName}” now`}
      support={
        <span className="row">
          <Spinner label="Publishing" />
          <span>{elapsed(run.startedAt, now)} elapsed</span>
        </span>
      }
    >
      <div className="row">
        {kind === "changelog" ? (
          <>
            <Badge tone="info">Repositories covered {formatCount(run.counters.reposCovered)}</Badge>
            <Badge tone="neutral">Commits seen {formatCount(run.counters.commitsSeen)}</Badge>
            <Badge tone="neutral">Dropped automatically {formatCount(run.counters.commitsDropped)}</Badge>
            <Badge tone="neutral">Read, not reported {formatCount(run.counters.commitsRoutine)}</Badge>
          </>
        ) : (
          // Three badges the document kinds cannot fill, replaced by the one thing that does climb: the
          // repository is read, the model is called, and the page's sections land at the end. Showing
          // "Commits seen 0" for two minutes would look like a run that had failed to find anything.
          <Badge tone="info">
            Repository read {run.counters.reposCovered > 0 ? "yes" : "not yet"}
          </Badge>
        )}
        {/* "Written", not "Published": delivery is the LAST step of a run, so nothing on a page has
            changed while this panel is on screen. A green "Published 9" beside an unchanged Confluence
            page is the application contradicting itself to the one person watching it happen. */}
        <Badge tone="success">
          {kind === "changelog" ? "Items written" : "Page sections written"}{" "}
          {formatCount(run.counters.itemsPublished)}
        </Badge>
        {run.counters.reposFailed > 0 ? (
          <Badge tone="warning">Repositories failed {formatCount(run.counters.reposFailed)}</Badge>
        ) : null}
      </div>
      <p className="field__hint">
        Started {formatDateTime(run.startedAt)}. This is running on the server, so you can leave this page or
        close the tab — the run carries on, and its progress is here when you come back. Pressing the button
        again while it runs starts nothing: one update publishes at a time.
      </p>
    </Card>
  );
}

/**
 * A run that has just landed, reported once.
 *
 * Shown as a banner rather than a toast because a toast that fired while the tab was in the background is a
 * toast nobody read — and the completion of a several-minute publish is exactly the thing somebody stepped
 * away from. The toast fires too; this is what is still on screen when they return.
 */
export function RunOutcome({
  run,
  kind = "changelog",
}: {
  readonly run: Run;
  readonly kind?: UpdateKind;
}): JSX.Element {
  const took = formatDuration(run.startedAt, run.finishedAt);

  if (kind !== "changelog") return <DocumentOutcome run={run} took={took} />;

  if (run.status === "failed") {
    return (
      <Banner tone="danger">
        The update failed after {took}. {run.error ?? "No reason was recorded."} Nothing was published to any
        destination that had not already succeeded, and the watermark was not advanced — so the next run
        covers the same commits again.
      </Banner>
    );
  }

  if (run.status === "skipped") {
    return (
      <Banner tone="info">
        Nothing to publish. The run covered {formatCount(run.counters.commitsSeen)} commit
        {run.counters.commitsSeen === 1 ? "" : "s"} in {took}, and none of them was worth reporting, so no
        update was posted.
      </Banner>
    );
  }

  return (
    <Banner tone="info">
      Published in {took}: {formatCount(run.counters.itemsPublished)} item
      {run.counters.itemsPublished === 1 ? "" : "s"} from {formatCount(run.counters.commitsSeen)} commit
      {run.counters.commitsSeen === 1 ? "" : "s"} across {formatCount(run.counters.reposCovered)} repositor
      {run.counters.reposCovered === 1 ? "y" : "ies"}
      {run.counters.commitsDropped > 0
        ? `, with ${formatCount(run.counters.commitsDropped)} dropped automatically`
        : ""}
      {run.counters.commitsRoutine > 0
        ? ` and ${formatCount(run.counters.commitsRoutine)} read and passed over as routine`
        : ""}
      {run.counters.reposFailed > 0
        ? `. ${formatCount(run.counters.reposFailed)} repository read failed — the rest still published`
        : ""}
      .
    </Banner>
  );
}

/**
 * The same three outcomes for an update that reads a document rather than commits.
 *
 * Not a paraphrase of the above with different nouns — one clause of the changelog's failure sentence is
 * actively WRONG here, and it is the clause a reader would act on. A failed changelog run leaves its
 * watermark where it was, so "the next run covers the same commits again" is a promise the engine keeps.
 * A document run has no watermark: `ticket_document_marks` is written the instant the fact it records
 * becomes true — a file was read, or a file was pushed — because both are facts about the REPOSITORY, and
 * a repository does not roll back because a Confluence publish failed afterwards. So a run that pushed a
 * document and then failed has left a commit standing, and the next run will relay that commit's file
 * rather than write it again. Telling somebody the opposite is how they go looking for a commit that is
 * already there.
 *
 * `itemsPublished` counts the composed page's SECTIONS here, not bullets — `engine/kinds/document.ts`
 * files `blocks.length` into it — so it is named for what it is.
 */
function DocumentOutcome({ run, took }: { readonly run: Run; readonly took: string }): JSX.Element {
  if (run.status === "failed") {
    return (
      <Banner tone="danger">
        The update failed after {took}. {run.error ?? "No reason was recorded."} The page was not
        published. Anything already committed to the repository stays there — a document is written before
        the page is, and a failure afterwards does not take it back; the next run relays it rather than
        writing it again.
      </Banner>
    );
  }

  if (run.status === "skipped") {
    return (
      <Banner tone="info">
        Nothing to publish. The run took {took} and composed no page, so nothing was written to the
        repository and nothing was posted. The run below records what it found.
      </Banner>
    );
  }

  return (
    <Banner tone="info">
      Published in {took}: the document was composed into {formatCount(run.counters.itemsPublished)}{" "}
      section{run.counters.itemsPublished === 1 ? "" : "s"} and the page was rewritten from it. Open the
      run below for which file it read, in which repository, and whether this update wrote that file
      itself
      {run.counters.truncated
        ? ". The model stopped before the end of its answer, so part of the document is not on the page"
        : ""}
      .
    </Banner>
  );
}
